import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  BASE_CURRENCY,
  convertFromBaseCurrency,
  formatFromBaseCurrency,
  resolveCurrencyForCountry,
  setEurToUahRate,
  getEurToUahRate,
  type Currency,
  type FormatPriceOptions,
} from '../lib/currency'
import { getLastKnownCountry } from '../lib/analytics'

type CurrencyContextValue = {
  country: string
  currency: Currency
  baseCurrency: Currency
  eurToUahRate: number
  convertPrice: (amountInBase: number) => number
  formatPrice: (amountInBase: number, options?: FormatPriceOptions) => string
}

const RATE_STORAGE_KEY = 'artist_eur_to_uah_rate'

const CurrencyContext = createContext<CurrencyContextValue>({
  country: '',
  currency: BASE_CURRENCY,
  baseCurrency: BASE_CURRENCY,
  eurToUahRate: getEurToUahRate(),
  convertPrice: (value: number) => value,
  formatPrice: (value: number) => formatFromBaseCurrency(value, BASE_CURRENCY),
})

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountry] = useState(() => getLastKnownCountry() || '')
  const [eurToUahRate, setRateState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = Number(window.localStorage.getItem(RATE_STORAGE_KEY))
      if (Number.isFinite(stored) && stored > 0) {
        setEurToUahRate(stored)
        return stored
      }
    }
    return getEurToUahRate()
  })

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<string | undefined>).detail || ''
      setCountry(detail)
    }
    window.addEventListener('artist-country-update', handler as EventListener)
    return () => {
      window.removeEventListener('artist-country-update', handler as EventListener)
    }
  }, [])

  const currency = useMemo(() => resolveCurrencyForCountry(country), [country])
  const convertPrice = useCallback(
    (amountInBase: number) => convertFromBaseCurrency(amountInBase, currency),
    [currency, eurToUahRate],
  )
  const formatPrice = useCallback(
    (amountInBase: number, options?: FormatPriceOptions) => formatFromBaseCurrency(amountInBase, currency, options),
    [currency, eurToUahRate],
  )

  useEffect(() => {
    let cancelled = false
    const loadRate = async () => {
      try {
        const response = await fetch('/api/settings/currency-rate')
        if (!response.ok) return
        const data = await response.json()
        const rate = Number(data?.eurToUahRate)
        if (!Number.isFinite(rate) || rate <= 0) return
        if (cancelled) return
        setEurToUahRate(rate)
        setRateState(rate)
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(RATE_STORAGE_KEY, String(rate))
        }
      } catch (_error) {
        // ignore fetch errors; fallback rate remains
      }
    }
    loadRate()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const handler = (event: Event) => {
      const nextRate = Number((event as CustomEvent<number>).detail)
      if (!Number.isFinite(nextRate) || nextRate <= 0) return
      setEurToUahRate(nextRate)
      setRateState(nextRate)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(RATE_STORAGE_KEY, String(nextRate))
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('currency-rate-updated', handler as EventListener)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('currency-rate-updated', handler as EventListener)
      }
    }
  }, [])

  const value = useMemo(
    () => ({
      country,
      currency,
      baseCurrency: BASE_CURRENCY,
      eurToUahRate,
      convertPrice,
      formatPrice,
    }),
    [country, currency, eurToUahRate, convertPrice, formatPrice],
  )

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

export function useCurrency() {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}
